/**
 * Миниатюра упражнения — одна на все экраны.
 *
 * Зачем. Человек видит название («Тяга горизонтального блока») и не всегда
 * знает, что за ним стоит. Миниатюра отвечает на это раньше слов — поэтому
 * она стоит везде, где на экране есть упражнение: в правке тренировки, в
 * составе программы, на «Сегодня», на тренировке, в истории. Копия на каждом
 * экране рано или поздно разошлась бы — поэтому миниатюра живёт здесь.
 *
 * ЧТО НА НЕЙ. Анимированная пиктограмма (`ExerciseFigure`): фигурка делает
 * движение. Раньше здесь стоял уменьшенный рисунок из свободного набора, и у
 * него было три беды: в плитке 56 px штриховка сливалась в пятно, у семи
 * движений рисунка не было вовсе («?»), а сам файл надо было скачать — в зале
 * без связи на его месте оставалась пустая рамка. Пиктограмма нарисована кодом
 * и едет вместе со сборкой: она есть у каждого каталожного движения и не
 * зависит от сети. Подробные рисунки остались там, где они нужны, — в
 * просмотре техники.
 *
 * ЭТО ОТДЕЛЬНАЯ КНОПКА. Посмотреть и сделать — разные действия, а рядом с
 * картинкой всегда стоит что-то необратимое или просто другое: выбор замены,
 * «Убрать», тап по подходу в истории. Поэтому миниатюра — своя кнопка со своей
 * зоной касания, и вкладывать её в чужую кнопку нельзя.
 *
 * У личного движения кнопки нет: технику за человека приложение не сочиняет,
 * и как выглядит придуманное им упражнение, знать не может.
 */

import { ExerciseFigure } from './ExerciseFigure'
import { hasTechnique } from './TechniqueCard'

/**
 * Минимум, по которому рисуется миниатюра.
 *
 * Не весь `Exercise`: на «Целях» движение приходит вариантом цели, в «Истории» —
 * номером из подхода. `is_custom` необязателен: личные движения живут с номера
 * 1 000 000, и подсказок по ним нет в принципе.
 */
export interface ExerciseRef {
  id: number
  name: string
  is_custom?: boolean
}

/** Есть ли что открыть по этому движению: текст техники есть у всех каталожных. */
export function hasPeek(exercise: ExerciseRef): boolean {
  return !exercise.is_custom && hasTechnique(exercise.id)
}

export function ExerciseThumb({
  exercise,
  onPeek,
  expanded,
  controls,
}: {
  exercise: ExerciseRef
  /** Кнопка передаётся, чтобы экран мог удержать её под пальцем. */
  onPeek: (button: HTMLButtonElement) => void
  expanded?: boolean
  controls?: string
}) {
  if (!hasPeek(exercise)) return null

  return (
    <button
      type="button"
      className="xp-thumb"
      aria-expanded={expanded}
      aria-controls={controls}
      aria-label={`Как выглядит и как делать: ${exercise.name}`}
      onClick={(event) => onPeek(event.currentTarget)}
    >
      <ExerciseFigure exerciseId={exercise.id} />
    </button>
  )
}

/** Пустое место размером с миниатюру: у повтора движения подряд выравнивание не прыгает. */
export function ThumbGap() {
  return <span className="xp-thumb-gap" aria-hidden="true" />
}
